import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/apiClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import {
  Package,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Users,
  Calendar,
  TrendingUp,
} from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { cn } from "@/lib/utils";

interface OrdersSummaryProps {
  dateRange: {
    start: Date;
    end: Date;
  };
}

export function OrdersSummary({ dateRange }: OrdersSummaryProps) {
  const { data: workOrders } = useQuery({
    queryKey: ["work-orders-summary", dateRange.start, dateRange.end],
    queryFn: async () => {
      const { data, error } = await api
        .from("work_orders")
        .select(`
          *,
          profiles:assigned_operator(full_name, email)
        `)
        .gte("created_at", dateRange.start.toISOString())
        .lte("created_at", dateRange.end.toISOString())
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  // Status distribution
  const statusCounts = {
    pending: workOrders?.filter((o) => o.status === "pending").length || 0,
    in_progress: workOrders?.filter((o) => o.status === "in_progress").length || 0,
    completed: workOrders?.filter((o) => o.status === "completed").length || 0,
    cancelled: workOrders?.filter((o) => o.status === "cancelled").length || 0,
    on_hold: workOrders?.filter((o) => o.status === "on_hold").length || 0,
  };

  const statusData = [
    { name: "Pendientes", value: statusCounts.pending, color: "#6b7280" },
    { name: "En Progreso", value: statusCounts.in_progress, color: "#3b82f6" },
    { name: "Completadas", value: statusCounts.completed, color: "#22c55e" },
    { name: "Canceladas", value: statusCounts.cancelled, color: "#ef4444" },
    { name: "En Espera", value: statusCounts.on_hold, color: "#eab308" },
  ].filter((d) => d.value > 0);

  // Priority distribution
  const priorityCounts = {
    urgent: workOrders?.filter((o) => o.priority === "urgent").length || 0,
    high: workOrders?.filter((o) => o.priority === "high").length || 0,
    normal: workOrders?.filter((o) => o.priority === "normal").length || 0,
    low: workOrders?.filter((o) => o.priority === "low").length || 0,
  };

  const priorityData = [
    { name: "Urgente", value: priorityCounts.urgent, color: "#ef4444" },
    { name: "Alta", value: priorityCounts.high, color: "#f97316" },
    { name: "Normal", value: priorityCounts.normal, color: "#3b82f6" },
    { name: "Baja", value: priorityCounts.low, color: "#6b7280" },
  ].filter((d) => d.value > 0);

  // Orders by customer
  const customerOrders = workOrders?.reduce((acc: Record<string, number>, order) => {
    const customer = order.customer || "Sin cliente";
    acc[customer] = (acc[customer] || 0) + 1;
    return acc;
  }, {}) || {};

  const customerData = Object.entries(customerOrders)
    .map(([customer, count]) => ({ customer, ordenes: count }))
    .sort((a, b) => b.ordenes - a.ordenes)
    .slice(0, 8);

  // Orders by product
  const productOrders = workOrders?.reduce((acc: Record<string, number>, order) => {
    const product = order.product_name || "Sin producto";
    acc[product] = (acc[product] || 0) + 1;
    return acc;
  }, {}) || {};

  const productData = Object.entries(productOrders)
    .map(([product, count]) => ({ product: product.substring(0, 20), ordenes: count }))
    .sort((a, b) => b.ordenes - a.ordenes)
    .slice(0, 5);

  // Completion rate
  const totalOrders = workOrders?.length || 0;
  const completedOrders = statusCounts.completed;
  const completionRate = totalOrders > 0 ? (completedOrders / totalOrders) * 100 : 0;

  // On-time delivery (simplified - orders completed before due date)
  const onTimeOrders = workOrders?.filter((o) => {
    if (o.status !== "completed" || !o.due_date) return false;
    const completed = o.updated_at ? new Date(o.updated_at) : new Date();
    const due = new Date(o.due_date);
    return completed <= due;
  }).length || 0;

  const onTimeRate = completedOrders > 0 ? (onTimeOrders / completedOrders) * 100 : 0;

  // Overdue orders
  const overdueOrders = workOrders?.filter((o) => {
    if (o.status === "completed" || o.status === "cancelled" || !o.due_date) return false;
    return new Date(o.due_date) < new Date();
  }) || [];

  const COLORS = ["#6b7280", "#3b82f6", "#22c55e", "#ef4444", "#eab308"];

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <Package className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{totalOrders}</p>
                <p className="text-sm text-muted-foreground">Total Ordenes</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/10">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{completionRate.toFixed(0)}%</p>
                <p className="text-sm text-muted-foreground">Tasa Completado</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/10">
                <Clock className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{onTimeRate.toFixed(0)}%</p>
                <p className="text-sm text-muted-foreground">Entrega a Tiempo</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className={cn(
                "p-2 rounded-lg",
                overdueOrders.length > 0 ? "bg-red-500/10" : "bg-green-500/10"
              )}>
                <AlertTriangle className={cn(
                  "w-5 h-5",
                  overdueOrders.length > 0 ? "text-red-500" : "text-green-500"
                )} />
              </div>
              <div>
                <p className="text-2xl font-bold">{overdueOrders.length}</p>
                <p className="text-sm text-muted-foreground">Atrasadas</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Status Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Distribucion por Estado</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    dataKey="value"
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    labelLine={false}
                  >
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--background))",
                      border: "1px solid hsl(var(--border))",
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap justify-center gap-4 mt-4">
              {statusData.map((status) => (
                <div key={status.name} className="flex items-center gap-2 text-sm">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: status.color }} />
                  <span>{status.name}: {status.value}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Priority Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Distribucion por Prioridad</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {priorityData.map((priority) => (
                <div key={priority.name} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: priority.color }}
                      />
                      <span>{priority.name}</span>
                    </div>
                    <span className="font-bold">{priority.value}</span>
                  </div>
                  <Progress
                    value={(priority.value / totalOrders) * 100}
                    className="h-2"
                    style={{ ["--progress-background" as any]: priority.color }}
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Orders by Customer */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Ordenes por Cliente
          </CardTitle>
        </CardHeader>
        <CardContent>
          {customerData.length > 0 ? (
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={customerData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="customer" type="category" width={120} tick={{ fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--background))",
                      border: "1px solid hsl(var(--border))",
                    }}
                  />
                  <Bar dataKey="ordenes" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-muted-foreground">
              No hay datos de clientes
            </div>
          )}
        </CardContent>
      </Card>

      {/* Top Products */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Top Productos
          </CardTitle>
        </CardHeader>
        <CardContent>
          {productData.length > 0 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={productData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="product" tick={{ fontSize: 11 }} />
                  <YAxis />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--background))",
                      border: "1px solid hsl(var(--border))",
                    }}
                  />
                  <Bar dataKey="ordenes" name="Ordenes" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-muted-foreground">
              No hay datos de productos
            </div>
          )}
        </CardContent>
      </Card>

      {/* Overdue Orders List */}
      {overdueOrders.length > 0 && (
        <Card className="border-red-500/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-500">
              <AlertTriangle className="w-5 h-5" />
              Ordenes Atrasadas ({overdueOrders.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {overdueOrders.slice(0, 5).map((order) => (
                <div
                  key={order.id}
                  className="flex items-center justify-between p-3 rounded-lg border bg-red-500/5"
                >
                  <div>
                    <p className="font-medium">{order.order_number}</p>
                    <p className="text-sm text-muted-foreground">{order.product_name}</p>
                  </div>
                  <div className="text-right">
                    <Badge variant="destructive">Atrasada</Badge>
                    <p className="text-xs text-muted-foreground mt-1">
                      Vencio: {format(new Date(order.due_date), "dd/MM/yyyy", { locale: es })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
